/**
 * Size Region Toggle — AU / US / UK size switching for the variant picker.
 *
 * Reads size conversion data from an inline <script data-size-conversions> tag
 * and the product gender from data-gender on the .variant-picker container.
 *
 * Rewrites size button labels on region change and emits a `size:selected`
 * custom event with cart line item formatting. Region always defaults to AU
 * on each PDP load — selection is not persisted across products.
 */

class SizeRegionToggle {
  constructor(container) {
    this.container = container;
    this.gender = container.dataset.gender || '';
    this.kidsYouth = container.dataset.kidsYouth === 'true';
    this.sizeConversions = [];
    this.region = 'AU';

    const dataEl = container.querySelector('[data-size-conversions]');
    if (dataEl) {
      try {
        this.sizeConversions = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[SizeRegionToggle] Failed to parse size conversions:', e);
      }
    }

    this.sizeHeader = container.querySelector('.variant-picker__size-header');
    this.regionSelector = container.querySelector('[data-region-selector]');
    this.regionButtonsContainer = container.querySelector('[data-region-buttons]');
    this.sizeLabel = container.querySelector('[data-size-label]');
    this.selectedSizeDisplay = container.querySelector('[data-selected-size-display]');
    this.sizeProperty = container.querySelector('[data-size-property]');

    if (!this.sizeHeader || !this.sizeConversions.length) return;

    this.init();
  }

  init() {
    this.buildRegionTabs();
    this.renderSizeLabels();
    this.updateSizeDisplay();
    this.bindEvents();
  }

  /* ── Region tabs ─────────────────────────────────────────── */

  getAvailableRegions() {
    var regions = ['AU'];
    this.sizeConversions.forEach(function (sc) {
      (sc.multiRegionConversions || []).forEach(function (mrc) {
        if (mrc.region && regions.indexOf(mrc.region) === -1) {
          regions.push(mrc.region);
        }
      });
    });
    return regions;
  }

  buildRegionTabs() {
    var regions = this.getAvailableRegions();
    if (regions.length <= 1) return;

    var self = this;
    regions.forEach(function (r) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'variant-picker__region-btn' + (r === self.region ? ' variant-picker__region-btn--active' : '');
      btn.textContent = r;
      btn.dataset.region = r;
      self.regionButtonsContainer.appendChild(btn);
    });

    this.regionSelector.style.display = '';
  }

  /* ── Size conversion (ported from reference getMultiRegionSize) ── */

  getMultiRegionSize(size, genderKey) {
    var isWomens = this.gender === 'Womens';

    if (this.region !== 'AU') {
      var sc = this.sizeConversions.find(function (s) {
        return isWomens ? s.womensSize == size : s.mensSize == size;
      });
      var mrc = (sc && sc.multiRegionConversions || []).find(function (m) {
        return m.region === this.region;
      }.bind(this));
      return genderKey === 'm' ? (mrc && mrc.mensSize) : (mrc && mrc.womensSize);
    }

    if (genderKey === 'm') return size;
    var sc2 = this.sizeConversions.find(function (s) {
      return isWomens ? s.womensSize == size : s.mensSize == size;
    });
    return sc2 && sc2.womensSize;
  }

  /* ── Format size for button display (ported from reference getSize) ── */

  formatSize(size) {
    if (!size) return null;

    if (this.kidsYouth) {
      var match = String(size).match(/^(.*?)\s*(\d+(?:\.\d+)?)\s*$/);
      if (!match) return size;
      var group = match[1].trim();
      var num = match[2];
      if (this.region === 'AU' || !this.sizeConversions.length) {
        return group ? group + ' ' + num : num;
      }
      var sc = this.sizeConversions.find(function (s) { return s.mensSize == num; });
      var mrc = (sc && sc.multiRegionConversions || []).find(function (m) {
        return m.region === this.region;
      }.bind(this));
      var converted = mrc && mrc.mensSize;
      if (!converted) return null;
      return group ? group + ' ' + converted : converted;
    }

    if (this.gender && this.sizeConversions.length) {
      if (this.gender === 'Unisex') {
        var womensSize = null;
        var sc = this.sizeConversions.find(function (s) { return s.mensSize == size; });
        if (sc) womensSize = sc.womensSize;

        if (womensSize) {
          return 'M ' + this.getMultiRegionSize(size, 'm') + ' | W ' + this.getMultiRegionSize(size, 'w');
        }
        return 'M ' + this.getMultiRegionSize(size, 'm');
      }

      if (this.gender === 'Womens') {
        var ws = this.getMultiRegionSize(size, 'w');
        return ws ? 'Womens ' + ws : null;
      }

      return this.gender + ' ' + this.getMultiRegionSize(size, 'm');
    }

    return size;
  }

  /* ── Format cart line item property (ported from reference getCartSizeAttribute) ── */

  formatCartAttribute(size) {
    if (this.kidsYouth) {
      var match = String(size).match(/^(.*?)\s*(\d+(?:\.\d+)?)\s*$/);
      if (!match) return size;
      var group = match[1].trim();
      var num = match[2];
      var auPart = group ? group + ' AU ' + num : 'AU ' + num;
      if (this.region === 'AU' || !this.sizeConversions.length) return auPart;
      var sc = this.sizeConversions.find(function (s) { return s.mensSize == num; });
      var mrc = (sc && sc.multiRegionConversions || []).find(function (m) {
        return m.region === this.region;
      }.bind(this));
      var converted = mrc && mrc.mensSize;
      if (!converted) return auPart;
      var convertedPart = group ? group + ' ' + this.region + ' ' + converted : this.region + ' ' + converted;
      return auPart + ' (' + convertedPart + ')';
    }

    if (!this.gender || !this.sizeConversions.length) return size;

    var sizeAU = size;
    var sizeConverted = size;

    if (this.gender === 'Unisex') {
      var sc = this.sizeConversions.find(function (s) { return s.mensSize == size; });
      var womensSizeAU = sc && sc.womensSize;
      var womensConverted = womensSizeAU;

      if (this.region !== 'AU') {
        var mrc = (sc && sc.multiRegionConversions || []).find(function (m) {
          return m.region === this.region;
        }.bind(this));
        sizeConverted = mrc && mrc.mensSize;
        womensConverted = mrc && mrc.womensSize;
      }

      if (womensSizeAU) {
        var auPart = 'Mens AU ' + sizeAU + ' | Womens AU ' + womensSizeAU;
        if (this.region === 'AU') return auPart;
        return auPart + ' (Mens ' + this.region + ' ' + sizeConverted + ' | Womens ' + this.region + ' ' + womensConverted + ')';
      }
      var auPartM = 'Mens AU ' + sizeAU;
      if (this.region === 'AU') return auPartM;
      return auPartM + ' (Mens ' + this.region + ' ' + sizeConverted + ')';
    }

    if (this.gender === 'Womens') {
      if (this.region !== 'AU') {
        var scW = this.sizeConversions.find(function (s) { return s.womensSize == size; });
        var mrcW = (scW && scW.multiRegionConversions || []).find(function (m) {
          return m.region === this.region;
        }.bind(this));
        sizeConverted = mrcW && mrcW.womensSize;
      }
      var auPartW = 'Womens AU ' + sizeAU;
      if (this.region === 'AU') return auPartW;
      return auPartW + ' (Womens ' + this.region + ' ' + sizeConverted + ')';
    }

    if (this.gender === 'Mens') {
      if (this.region !== 'AU') {
        var scM = this.sizeConversions.find(function (s) { return s.mensSize == size; });
        var mrcM = (scM && scM.multiRegionConversions || []).find(function (m) {
          return m.region === this.region;
        }.bind(this));
        sizeConverted = mrcM && mrcM.mensSize;
      }
      var auPartMens = 'Mens AU ' + sizeAU;
      if (this.region === 'AU') return auPartMens;
      return auPartMens + ' (Mens ' + this.region + ' ' + sizeConverted + ')';
    }

    return size;
  }

  /* ── Rendering ───────────────────────────────────────────── */

  renderSizeLabels() {
    var self = this;
    this.container.querySelectorAll('.variant-picker__option-button--button[data-au-size]').forEach(function (label) {
      var rawSize = label.dataset.auSize;
      var formatted = self.formatSize(rawSize);
      var optionEl = label.closest('.variant-picker__option');
      if (formatted) {
        label.textContent = formatted;
        if (optionEl) optionEl.style.display = '';
      } else {
        if (optionEl) optionEl.style.display = 'none';
      }
    });
  }

  getCheckedAuSize(checkedInput) {
    if (!checkedInput) return null;
    var label = this.container.querySelector('label[for="' + checkedInput.id + '"]');
    if (label && label.dataset.auSize) return label.dataset.auSize;
    return checkedInput.value;
  }

  updateSizeDisplay() {
    if (this.sizeLabel) {
      this.sizeLabel.textContent = this.region + ' Size:';
    }

    var checkedInput = this.container.querySelector(
      '.variant-picker__button input[type="radio"]:checked'
    );
    if (checkedInput && this.selectedSizeDisplay) {
      var sizeValue = this.getCheckedAuSize(checkedInput);
      var formatted = this.formatSize(sizeValue);
      if (formatted) {
        this.selectedSizeDisplay.textContent = formatted;
      }
    }

    this.updateCartProperty();
  }

  updateCartProperty() {
    var checkedInput = this.container.querySelector(
      '.variant-picker__button input[type="radio"]:checked'
    );
    if (checkedInput && this.sizeProperty) {
      var sizeValue = this.getCheckedAuSize(checkedInput);
      this.sizeProperty.value = this.formatCartAttribute(sizeValue);
      this.sizeProperty.disabled = false;
    }
  }

  /* ── Events ──────────────────────────────────────────────── */

  bindEvents() {
    var self = this;

    this.regionButtonsContainer.addEventListener('click', function (e) {
      var btn = e.target.closest('.variant-picker__region-btn');
      if (!btn) return;

      self.region = btn.dataset.region;

      self.regionButtonsContainer.querySelectorAll('.variant-picker__region-btn').forEach(function (b) {
        b.classList.remove('variant-picker__region-btn--active');
      });
      btn.classList.add('variant-picker__region-btn--active');

      self.renderSizeLabels();
      self.updateSizeDisplay();

      self.container.dispatchEvent(new CustomEvent('region:changed', {
        detail: { region: self.region },
        bubbles: true,
      }));
    });

    this.container.addEventListener('change', function (e) {
      var radio = e.target;
      if (radio.type !== 'radio') return;

      var field = radio.closest('.variant-picker__field');
      if (!field || !field.querySelector('.variant-picker__size-header')) return;

      self.updateSizeDisplay();

      var sizeValue = self.getCheckedAuSize(radio);
      var formatted = self.formatSize(sizeValue);
      var cartAttr = self.formatCartAttribute(sizeValue);
      self.container.dispatchEvent(new CustomEvent('size:selected', {
        detail: {
          name: 'Size',
          value: radio.value,
          formattedSize: formatted,
          cartSizeAttribute: cartAttr,
          region: self.region,
        },
        bubbles: true,
      }));
    });
  }
}

/* ── Initialize ────────────────────────────────────────────── */

window.SizeRegionToggle = SizeRegionToggle;

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.variant-picker[data-gender]').forEach(function (el) {
    new SizeRegionToggle(el);
  });
});
